"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeLayout = computeLayout;
const alloc_lanes_1 = require("./alloc-lanes");
const assign_columns_1 = require("./assign-columns");
const order_rows_1 = require("./order-rows");
const voltage_1 = require("./voltage");
const constants_1 = require("../style/constants");
function computeLayout(blocks, edges, placement, spacing, voltageScale) {
    const warnings = [];
    const kindOf = new Map(blocks.map((b) => [b.id, b.kind]));
    let used;
    const col = new Map();
    const slot = new Map();
    let nCols;
    let maxRows;
    let hub;
    const hRows = new Map();
    const wFac = new Map();
    if (placement !== undefined && placement.cells !== undefined) {
        const spans = placement.spans ?? {};
        const connected = new Set();
        for (const e of edges) {
            connected.add(e.a);
            connected.add(e.b);
        }
        used = blocks.filter((b) => connected.has(b.id) && placement.cells[b.id] !== undefined);
        const dropped = blocks.filter((b) => connected.has(b.id) && placement.cells[b.id] === undefined);
        for (const b of dropped) {
            warnings.push(`Block '${b.id}' is connected but has no placement cell; dropped from the layout`);
        }
        const taken = new Set();
        const rowSet = new Set();
        const colSet = new Set();
        for (const b of used) {
            const [c, r0] = placement.cells[b.id];
            let r = r0;
            const h = Math.max(1, Math.round(spans[b.id]?.h ?? 1));
            const w = Math.max(1, spans[b.id]?.w ?? 1);
            const clash = (rr) => {
                for (let k = 0; k < h; k++) {
                    if (taken.has(`${c},${rr + k}`)) {
                        return true;
                    }
                }
                return false;
            };
            while (clash(r)) {
                r += 1;
            }
            for (let k = 0; k < h; k++) {
                taken.add(`${c},${r + k}`);
                rowSet.add(r + k);
            }
            colSet.add(c);
            col.set(b.id, c);
            slot.set(b.id, r);
            hRows.set(b.id, h);
            wFac.set(b.id, w);
        }
        const cVals = [...colSet].sort((a, b) => a - b);
        const rVals = [...rowSet].sort((a, b) => a - b);
        const cMap = new Map(cVals.map((v, i) => [v, i]));
        const rMap = new Map(rVals.map((v, i) => [v, i]));
        maxRows = 0;
        for (const id of col.keys()) {
            col.set(id, cMap.get(col.get(id) ?? 0) ?? 0);
            slot.set(id, rMap.get(slot.get(id) ?? 0) ?? 0);
            maxRows = Math.max(maxRows, (slot.get(id) ?? 0) + (hRows.get(id) ?? 1));
        }
        nCols = cVals.length;
        hub = placement.hub ?? null;
    }
    else {
        const assignment = (0, assign_columns_1.assignColumns)(blocks, edges);
        used = assignment.used;
        for (const [id, c] of assignment.col) {
            col.set(id, c);
        }
        nCols = assignment.nCols;
        hub = assignment.hub;
        const ordering = (0, order_rows_1.orderRows)(used, col, edges, nCols);
        for (const [id, r] of ordering.slot) {
            slot.set(id, r);
        }
        maxRows = ordering.maxRows;
        for (const b of used) {
            hRows.set(b.id, 1);
            wFac.set(b.id, 1);
        }
    }
    if (used.length === 0 || nCols === 0) {
        return {
            nodes: [],
            edges: [],
            chips: [],
            boardOutline: { x: constants_1.MARGIN, y: constants_1.MARGIN, w: 2 * constants_1.BOARD_PAD, h: 2 * constants_1.BOARD_PAD },
            canvas: { width: 2 * (constants_1.MARGIN + constants_1.BOARD_PAD), height: 2 * (constants_1.MARGIN + constants_1.BOARD_PAD) },
            warnings: [...warnings, 'No connected, placed blocks to lay out'],
        };
    }
    const S = spacing;
    const gapY = Math.round(constants_1.GAP_Y * S);
    const pitch = constants_1.NODE_H + gapY;
    const gutterMin = Math.round(constants_1.GUTTER_MIN * S);
    const laneStep = Math.round(constants_1.LANE_STEP * S);
    const nodeH = (id) => constants_1.NODE_H * (hRows.get(id) ?? 1) + gapY * ((hRows.get(id) ?? 1) - 1);
    const labelW = new Map();
    for (const b of used) {
        const big = (hRows.get(b.id) ?? 1) > 1 || (wFac.get(b.id) ?? 1) > 1;
        const nameW = (0, constants_1.estimateTextWidth)(b.name, big ? constants_1.NAME_FONT_SIZE_BIG : constants_1.NAME_FONT_SIZE);
        const subW = b.sub !== undefined ? (0, constants_1.estimateTextWidth)(b.sub, big ? constants_1.SUB_FONT_SIZE_BIG : constants_1.SUB_FONT_SIZE) : 0;
        labelW.set(b.id, Math.max(nameW, subW) + 2 * constants_1.BLOCK_LABEL_PAD_X);
    }
    const nodeW = (id) => Math.max(Math.round(constants_1.NODE_W * (wFac.get(id) ?? 1)), Math.ceil(Math.min(labelW.get(id) ?? 0, constants_1.NODE_W * constants_1.NODE_MAX_W_FACTOR)));
    const topY = constants_1.MARGIN + constants_1.BOARD_PAD + 14;
    const rowY = (r) => topY + r * pitch;
    const corrY = (k) => topY + k * pitch - gapY / 2;
    const colBlocks = Array.from({ length: nCols }, () => []);
    for (const b of used) {
        colBlocks[col.get(b.id) ?? 0].push(b.id);
    }
    const corridorClear = (k, cLo, cHi) => {
        for (let c = cLo; c <= cHi; c++) {
            for (const id of colBlocks[c]) {
                const s0 = slot.get(id) ?? 0;
                if (s0 < k && k <= s0 + (hRows.get(id) ?? 1) - 1) {
                    return false;
                }
            }
        }
        return true;
    };
    const plans = [];
    for (const e of edges) {
        let s = e.a;
        let t = e.b;
        let dir = 'both';
        if (e.fwd && !e.back) {
            dir = 'fwd';
        }
        else if (e.back && !e.fwd) {
            s = e.b;
            t = e.a;
            dir = 'fwd';
        }
        const cs = col.get(s);
        const ct = col.get(t);
        if (cs === undefined || ct === undefined) {
            continue;
        }
        plans.push({ e, s, t, dir, cs, ct, key: '' });
    }
    let eIdx = 0;
    for (const p of plans) {
        p.key = p.e.kind === 'rail' ? `r|${p.s}|${p.e.label}` : `e${eIdx++}`;
        if (p.e.kind === 'rail') {
            p.voltageRank = (0, voltage_1.voltageRankForLabel)(voltageScale, p.e.label);
        }
    }
    const portReq = new Map();
    const req = (id, side, farSlot, plan, which) => {
        const k = `${id}|${side}`;
        let rec = portReq.get(k);
        if (!rec) {
            rec = { id, reqs: [] };
            portReq.set(k, rec);
        }
        rec.reqs.push({ farSlot, plan, which });
    };
    for (const p of plans) {
        const sSlot = slot.get(p.s) ?? 0;
        const tSlot = slot.get(p.t) ?? 0;
        if (p.cs === p.ct && (tSlot === sSlot + (hRows.get(p.s) ?? 1) || sSlot === tSlot + (hRows.get(p.t) ?? 1))) {
            p.route = 'V';
            continue;
        }
        if (p.cs < p.ct) {
            req(p.s, 'R', tSlot, p, 'exit');
            req(p.t, 'L', sSlot, p, 'entry');
        }
        else if (p.cs > p.ct) {
            req(p.s, 'L', tSlot, p, 'exit');
            req(p.t, 'R', sSlot, p, 'entry');
        }
        else {
            req(p.s, 'R', tSlot, p, 'exit');
            req(p.t, 'R', sSlot, p, 'entry');
        }
    }
    const ports = new Map();
    for (const { id, reqs } of portReq.values()) {
        const units = new Map();
        reqs.forEach((r, i) => {
            const uk = r.which === 'exit' && r.plan.e.kind === 'rail' ? `rk:${r.plan.key}` : `solo:${i}`;
            let u = units.get(uk);
            if (!u) {
                u = { sum: 0, n: 0, members: [] };
                units.set(uk, u);
            }
            u.sum += r.farSlot;
            u.n += 1;
            u.members.push(r);
        });
        const ordered = [...units.values()].sort((a, b) => a.sum / a.n - b.sum / b.n);
        ordered.forEach((u, i) => {
            const y = rowY(slot.get(id) ?? 0) + ((i + 1) / (ordered.length + 1)) * nodeH(id);
            for (const r of u.members) {
                const rec = ports.get(r.plan) ?? {};
                rec[r.which] = y;
                ports.set(r.plan, rec);
            }
        });
    }
    const gutterItems = Array.from({ length: Math.max(nCols - 1, 1) }, () => []);
    for (const p of plans) {
        if (p.route === 'V') {
            continue;
        }
        const py = ports.get(p) ?? {};
        const exit = py.exit ?? 0;
        const entry = py.entry ?? 0;
        const railGroup = p.e.kind === 'rail' ? `${p.s}|${p.e.label}` : null;
        if (p.cs === p.ct) {
            const g = Math.max(0, Math.min(p.cs, nCols - 2));
            p.g1 = { lo: Math.min(exit, entry), hi: Math.max(exit, entry), group: null, plan: p, g };
            p.route = 'U';
            gutterItems[g].push(p.g1);
        }
        else if (Math.abs(p.cs - p.ct) === 1) {
            const g = Math.min(p.cs, p.ct);
            p.g1 = { lo: Math.min(exit, entry), hi: Math.max(exit, entry), group: railGroup, plan: p, g };
            p.route = 'HVH';
            gutterItems[g].push(p.g1);
        }
        else {
            const gA = p.cs < p.ct ? p.cs : p.cs - 1;
            const gB = p.cs < p.ct ? p.ct - 1 : p.ct;
            const cLo = Math.min(gA, gB) + 1;
            const cHi = Math.max(gA, gB);
            let k = Math.max(0, Math.min(maxRows, Math.round((entry - topY) / pitch)));
            for (const d of [0, 1, -1, 2, -2, 3, -3, 4, -4]) {
                const kk = k + d;
                if (kk >= 0 && kk <= maxRows && corridorClear(kk, cLo, cHi)) {
                    k = kk;
                    break;
                }
            }
            p.corr = k;
            const cy = corrY(k);
            p.g1 = { lo: Math.min(exit, cy), hi: Math.max(exit, cy), group: railGroup, plan: p, g: gA };
            p.g2 = { lo: Math.min(entry, cy), hi: Math.max(entry, cy), group: null, plan: p, g: gB };
            p.route = 'CORR';
            gutterItems[gA].push(p.g1);
            gutterItems[gB].push(p.g2);
        }
    }
    const laneIdx = new Map();
    const laneCount = [];
    gutterItems.forEach((items, g) => {
        const { out, n } = (0, alloc_lanes_1.allocLanes)(items, constants_1.GUTTER_LANE_PAD);
        for (const [it, l] of out) {
            laneIdx.set(it, l);
        }
        laneCount[g] = n;
    });
    const gutterW = laneCount.map((n) => Math.max(gutterMin, n * laneStep + Math.round(constants_1.GUTTER_EXTRA * S)));
    const colW = Array.from({ length: nCols }, (_, c) => Math.max(constants_1.NODE_W, ...colBlocks[c].map((id) => nodeW(id))));
    const colX = [];
    let cx = constants_1.MARGIN + constants_1.BOARD_PAD;
    for (let c = 0; c < nCols; c++) {
        colX[c] = cx;
        cx += colW[c] + (gutterW[c] ?? 0);
    }
    const laneX = (it) => {
        const g = it.g;
        const center = colX[g] + colW[g] + (gutterW[g] ?? gutterMin) / 2;
        return center + ((laneIdx.get(it) ?? 0) - (laneCount[g] - 1) / 2) * laneStep;
    };
    const corrItems = new Map();
    for (const p of plans) {
        if (p.route !== 'CORR' || p.g1 === undefined || p.g2 === undefined) {
            continue;
        }
        const x1 = laneX(p.g1);
        const x2 = laneX(p.g2);
        const k = p.corr ?? 0;
        const list = corrItems.get(k) ?? [];
        list.push({ lo: Math.min(x1, x2), hi: Math.max(x1, x2), group: null, plan: p });
        corrItems.set(k, list);
    }
    const corrLane = new Map();
    for (const k of [...corrItems.keys()].sort((a, b) => a - b)) {
        const items = corrItems.get(k) ?? [];
        const { out, n } = (0, alloc_lanes_1.allocLanes)(items, constants_1.CORRIDOR_LANE_PAD);
        for (const [it, l] of out) {
            corrLane.set(it.plan, corrY(k) + (l - (n - 1) / 2) * Math.round(constants_1.CORRIDOR_LANE_STEP * S));
        }
    }
    const xy = new Map();
    for (const b of used) {
        const c = col.get(b.id) ?? 0;
        xy.set(b.id, {
            x: colX[c] + (colW[c] - nodeW(b.id)) / 2,
            y: rowY(slot.get(b.id) ?? 0),
            w: nodeW(b.id),
            h: nodeH(b.id),
        });
    }
    const totW = cx - (gutterW[nCols - 1] ?? 0) + constants_1.BOARD_PAD + constants_1.MARGIN;
    const H = maxRows * pitch - gapY;
    const totH = topY + H + constants_1.BOARD_PAD + constants_1.MARGIN;
    let inner = used.filter((b) => kindOf.get(b.id) !== 'connector' || b.id === hub);
    if (inner.length === 0) {
        warnings.push('Every placed block is a connector; the board outline covers them all');
        inner = used;
    }
    const innerIds = new Set(inner.map((b) => b.id));
    const c0 = Math.min(...inner.map((b) => col.get(b.id) ?? 0));
    const c1 = Math.max(...inner.map((b) => col.get(b.id) ?? 0));
    const r0 = Math.min(...inner.map((b) => slot.get(b.id) ?? 0));
    const r1 = Math.max(...inner.map((b) => (slot.get(b.id) ?? 0) + (hRows.get(b.id) ?? 1) - 1));
    const bx0 = colX[c0] - constants_1.BOARD_PAD;
    const bx1 = colX[c1] + colW[c1] + constants_1.BOARD_PAD;
    let by0 = rowY(r0) - constants_1.BOARD_PAD - 6;
    let by1 = rowY(r1) + constants_1.NODE_H + constants_1.BOARD_PAD + 6;
    for (const [p, y] of corrLane) {
        if (innerIds.has(p.s) && innerIds.has(p.t)) {
            by0 = Math.min(by0, y - 16);
            by1 = Math.max(by1, y + 16);
        }
    }
    const layoutEdges = [];
    const chips = [];
    for (const p of plans) {
        const py = ports.get(p) ?? {};
        const sB = xy.get(p.s);
        const tB = xy.get(p.t);
        if (sB === undefined || tB === undefined) {
            continue;
        }
        let points;
        let lx;
        let ly;
        if (p.route === 'V') {
            const down = sB.y < tB.y;
            const xc = (Math.max(sB.x, tB.x) + Math.min(sB.x + sB.w, tB.x + tB.w)) / 2;
            const y1 = down ? sB.y + sB.h : sB.y;
            const y2 = down ? tB.y : tB.y + tB.h;
            points = [
                [xc, y1],
                [xc, y2],
            ];
            lx = xc;
            ly = (y1 + y2) / 2;
        }
        else if (p.route === 'U' && p.g1 !== undefined) {
            const x = laneX(p.g1);
            const exit = py.exit ?? 0;
            const entry = py.entry ?? 0;
            points = [
                [sB.x + sB.w, exit],
                [x, exit],
                [x, entry],
                [tB.x + tB.w, entry],
            ];
            lx = x;
            ly = (exit + entry) / 2;
        }
        else if (p.route === 'HVH' && p.g1 !== undefined) {
            const x = laneX(p.g1);
            const exit = py.exit ?? 0;
            const entry = py.entry ?? 0;
            const x1 = p.cs < p.ct ? sB.x + sB.w : sB.x;
            const x2 = p.cs < p.ct ? tB.x : tB.x + tB.w;
            points =
                exit === entry ?
                    [
                        [x1, exit],
                        [x2, exit],
                    ]
                    : [
                        [x1, exit],
                        [x, exit],
                        [x, entry],
                        [x2, entry],
                    ];
            lx = x;
            ly = exit === entry ? exit : (exit + entry) / 2;
        }
        else if (p.route === 'CORR' && p.g1 !== undefined && p.g2 !== undefined) {
            const xA = laneX(p.g1);
            const xB = laneX(p.g2);
            const cy = corrLane.get(p) ?? corrY(p.corr ?? 0);
            const exit = py.exit ?? 0;
            const entry = py.entry ?? 0;
            const x1 = p.cs < p.ct ? sB.x + sB.w : sB.x;
            const x2 = p.cs < p.ct ? tB.x : tB.x + tB.w;
            points = [
                [x1, exit],
                [xA, exit],
                [xA, cy],
                [xB, cy],
                [xB, entry],
                [x2, entry],
            ];
            const w = p.e.label.length * constants_1.CHIP_CHAR_W + constants_1.CHIP_PAD_W;
            lx = (xA + xB) / 2;
            ly = cy;
            for (const t of [0.5, 0.3, 0.7, 0.15, 0.85]) {
                const cx2 = xA + (xB - xA) * t;
                const hit = used.some((b) => {
                    const r = xy.get(b.id);
                    if (r === undefined) {
                        return false;
                    }
                    return cx2 + w / 2 > r.x - 4 && cx2 - w / 2 < r.x + r.w + 4 && cy + 9 > r.y - 4 && cy - 9 < r.y + r.h + 4;
                });
                if (!hit) {
                    lx = cx2;
                    break;
                }
            }
        }
        else {
            continue;
        }
        layoutEdges.push({
            key: p.key,
            kind: p.e.kind,
            points,
            arrowStart: p.dir === 'both',
            arrowEnd: true,
            nets: p.e.nets,
            ...(p.e.kind === 'rail' ? { voltageRank: p.voltageRank ?? null } : {}),
        });
        const gkey = `${p.s}|${p.e.label}|${p.e.kind}`;
        if (p.e.label !== '' && (p.e.kind !== 'rail' || !chips.some((c) => c.gkey === gkey))) {
            chips.push({
                x: lx,
                y: ly,
                text: p.e.label,
                gkey,
                kind: p.e.kind,
                key: p.key,
                ...(p.e.kind === 'rail' ? { voltageRank: p.voltageRank ?? null } : {}),
            });
        }
    }
    chips.sort((a, b) => a.x - b.x || a.y - b.y);
    for (let i = 0; i < chips.length; i++) {
        for (let j = 0; j < i; j++) {
            const a = chips[j];
            const b = chips[i];
            const wA = a.text.length * constants_1.CHIP_CHAR_W + constants_1.CHIP_PAD_W;
            const wB = b.text.length * constants_1.CHIP_CHAR_W + constants_1.CHIP_PAD_W;
            if (Math.abs(a.x - b.x) < (wA + wB) / 2 && Math.abs(a.y - b.y) < constants_1.CHIP_H) {
                b.y = a.y + constants_1.CHIP_DEOVERLAP_DY;
            }
        }
    }
    const nodes = used.map((b) => {
        const r = xy.get(b.id) ?? { x: 0, y: 0, w: constants_1.NODE_W, h: constants_1.NODE_H };
        return {
            id: b.id,
            kind: b.kind,
            x: r.x,
            y: r.y,
            w: r.w,
            h: r.h,
            big: (hRows.get(b.id) ?? 1) > 1 || (wFac.get(b.id) ?? 1) > 1,
        };
    });
    return {
        nodes,
        edges: layoutEdges,
        chips: chips.map(({ key, kind, x, y, text, voltageRank }) => ({
            key,
            kind,
            x,
            y,
            text,
            ...(voltageRank !== undefined ? { voltageRank } : {}),
        })),
        boardOutline: { x: bx0, y: by0, w: bx1 - bx0, h: by1 - by0 },
        canvas: { width: totW, height: totH },
        warnings,
    };
}
//# sourceMappingURL=compute-layout.js.map